import {assert} from 'Asserts';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  StatusTypeToDisplayNameMapping,
  Worksheet,
  WorksheetTaskStatusType,
} from 'notebook/worksheets/WorksheetConstants';
import {
  WorksheetViewQuery_worksheet_WorksheetNode_tasks_ProcessTaskNode_statusChanges_ProcessTaskStatusChangeNode as StatusChangeNode,
  WorksheetViewQuery_worksheet_WorksheetNode_tasks_ProcessTaskNode_procedureMethodExecutionInstance_ProcedureMethodExecutionInstanceNode_taskTransitionExtensions_TaskTransitionExtensionNode as TaskTransitionExtensionNode,
} from 'notebook/worksheets/workspace/__generated/WorksheetViewQuery';
import {HasAvatar} from 'ui-library/avatar/Avatar';

interface StatusChangeInfo {
  creator?: HasAvatar;
  timestamp: string;
}

function extractStatusChangeInfo(
  statusType: WorksheetTaskStatusType,
  statusChanges: Array<StatusChangeNode>,
  taskTransitionExtensions: Array<TaskTransitionExtensionNode>
): StatusChangeInfo | null {
  const creator = statusChanges.find(
    (statusChange) => statusChange.newStatus.displayName === StatusTypeToDisplayNameMapping[statusType]
  )?.creator;

  const timestamp = taskTransitionExtensions.find(
    (ext) => ext.statusDisplayName === StatusTypeToDisplayNameMapping[statusType]
  )?.timestampUtcIsoformat;

  return timestamp ? {creator, timestamp} : null;
}

export function extractProcedureRunDataFromTask(task?: Worksheet['tasks'][number]): {
  unitOperationName?: string;
  replicateNumber?: number;
  conditionName?: string;
  runStarted?: StatusChangeInfo | null;
  runEnded?: StatusChangeInfo | null;
  procedureType?: ProcedureType;
} {
  if (
    !task ||
    isInaccessibleInfo(task) ||
    !task.procedureMethodExecutionInstance ||
    isInaccessibleInfo(task.procedureMethodExecutionInstance)
  ) {
    return {};
  }

  const {procedureMethodExecutionInstance: pmei, statusChanges} = task;
  const {planMethodVersion, procedureRunConditionReplicates, procedureRun} = pmei;

  const runStarted = extractStatusChangeInfo(
    WorksheetTaskStatusType.IN_PROGRESS,
    statusChanges,
    pmei.taskTransitionExtensions
  );
  const runEnded = extractStatusChangeInfo(
    WorksheetTaskStatusType.COMPLETED,
    statusChanges,
    pmei.taskTransitionExtensions
  );

  assert(
    pmei.procedureRunConditionReplicates.length > 0,
    'pmei.procedureRunConditionReplicates should not be empty'
  );

  return {
    unitOperationName: planMethodVersion.name,
    replicateNumber: procedureRunConditionReplicates[0].replicateNumber,
    conditionName: procedureRunConditionReplicates[0].procedureRunCondition.name,
    runStarted,
    runEnded,
    procedureType: procedureRun.procedureType,
  };
}
